import { EngagementScoreTerminationReason } from '@quickplay/player-interface';
import { normalizeFiniteNumber, normalizeTiming, TimingSnapshot, ValidTimingSnapshot } from './EngagementScoreUtils';
import {
  createEngagementBehaviorTracker,
  EngagementObservation,
  deriveUnmutedActiveRatio,
  deriveVisibilityRatio,
} from './EngagementBehaviorTracker';
import { calculateEngagementScore, createEngagementScoreResult } from './EngagementScore';

interface VodEngagementScoreTrackerOptions {
  getTimingSnapshot: () => TimingSnapshot | undefined;
  getEligibleDuration: () => unknown;
  getObservation: () => EngagementObservation;
  onScore: (score: string) => void;
}

export function createVodEngagementScoreTracker({
  getTimingSnapshot,
  getEligibleDuration,
  getObservation,
  onScore,
}: VodEngagementScoreTrackerOptions) {
  const behaviorTracker = createEngagementBehaviorTracker({
    getObservation,
  });

  let baseline: ValidTimingSnapshot | undefined;
  let lastTiming: ValidTimingSnapshot | undefined;
  let lastEligibleDuration: number | undefined;
  let active = false;
  let timingInvalid = false;
  let finalized = false;

  return {
    start(): void {
      if (baseline || finalized) {
        return;
      }

      active = true;
      behaviorTracker.reset();
      establishBaseline();
    },

    observe(): void {
      if (!active || finalized || timingInvalid) {
        return;
      }

      if (!baseline) {
        establishBaseline();
        return;
      }

      const timing = normalizeTiming(getTimingSnapshot());
      const eligibleDuration = normalizeFiniteNumber(getEligibleDuration());
      if (
        !timing ||
        timing.playTime < lastTiming!.playTime ||
        timing.pauseTime < lastTiming!.pauseTime ||
        (eligibleDuration !== undefined &&
          lastEligibleDuration !== undefined &&
          eligibleDuration < lastEligibleDuration)
      ) {
        timingInvalid = true;
        return;
      }

      const playTimeDelta = timing.playTime - lastTiming!.playTime;
      behaviorTracker.observe(playTimeDelta);

      lastTiming = timing;
      if (eligibleDuration !== undefined) {
        lastEligibleDuration = eligibleDuration;
      }
    },

    finalize(terminationReason: EngagementScoreTerminationReason): void {
      if (!baseline || finalized) {
        return;
      }

      finalized = true;
      const finalTiming = normalizeTiming(getTimingSnapshot());
      const eligibleDuration = normalizeFiniteNumber(getEligibleDuration());
      const finalTimingInvalid =
        !finalTiming ||
        timingInvalid ||
        finalTiming.playTime < lastTiming!.playTime ||
        finalTiming.pauseTime < lastTiming!.pauseTime;

      const eligibleDurationInvalid =
        eligibleDuration === undefined ||
        eligibleDuration <= 0 ||
        (lastEligibleDuration !== undefined && eligibleDuration < lastEligibleDuration);

      if (finalTimingInvalid || eligibleDurationInvalid) {
        return;
      }

      const watchedDuration = finalTiming.playTime - baseline.playTime;
      const pauseDuration = finalTiming.pauseTime - baseline.pauseTime;

      const coverage = Math.min(watchedDuration / eligibleDuration, 1);
      const activeRatio = watchedDuration === 0 ? 0 : watchedDuration / (watchedDuration + pauseDuration);

      const behaviorAggregate = behaviorTracker.snapshot();
      const unmutedMetrics = deriveUnmutedActiveRatio(behaviorAggregate);
      const visibilityRatio = deriveVisibilityRatio(behaviorAggregate);

      const score = calculateEngagementScore({
        coverage,
        activeRatio,
        unmutedActiveRatio: unmutedMetrics.unmutedActiveRatio,
        visibilityRatio,
      });
      if (score === undefined) {
        return;
      }

      const engagementScore = createEngagementScoreResult({
        scope: 'vod',
        score,
        coverage,
        activeRatio,
        watchedDuration,
        pauseDuration,
        eligibleDuration,
        terminationReason,
        unmutedActiveRatio: unmutedMetrics.unmutedActiveRatio,
        mutedWatchDuration: unmutedMetrics.mutedWatchDuration,
        activePlaybackDuration: unmutedMetrics.activePlaybackDuration,
      });
      onScore(engagementScore);
    },

    reset(): void {
      baseline = undefined;
      lastTiming = undefined;
      lastEligibleDuration = undefined;
      timingInvalid = false;
      active = false;
      finalized = false;
      behaviorTracker.reset();
    },
  };

  function establishBaseline(): void {
    const timing = normalizeTiming(getTimingSnapshot());
    if (timing) {
      baseline = timing;
      lastTiming = timing;
    }

    const eligibleDuration = normalizeFiniteNumber(getEligibleDuration());
    if (eligibleDuration !== undefined) {
      lastEligibleDuration = eligibleDuration;
    }
  }
}
